/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * CalibrationService.js
 *
 * Converts pixel measurements to real-world centimeters
 * From thesis utils.ts convertToCm() and related functions
 *
 * Pipeline Step 3b: Kalibrierung (Calibration)
 * Input: Pixel measurements + EXIF + distance + angle
 * Output: Real-world measurements in centimeters
 */

import {
  getDefaultFocalLength,
  getDefaultFocal35mm,
} from '@/utils/ml/useFocalLength.js'
import focalLengthPresetsData from '@/constants/focalLengthPresets.json'

/**
 * Default conversion options from thesis utils.ts
 * based on iPhone 12 mini; dynamic values updated at runtime.
 */
const DefaultConvertOptions = {
  distance: 1.5,
  calibration: 3.375,
  calibration_distance: 200,
  orig_shape: [640, 640],
  mask_shape: [640, 640],
  angle: 20,
  sensorSize: (4 * ((43.27 * 4.2) / 26)) / 5, // 5.59mm
  focalLength: 4.2,
}

/**
 * Service for calibrating pixel measurements to real-world units
 */
export class CalibrationService {
  constructor() {
    this.defaultOptions = {
      ...DefaultConvertOptions,
      get focalLength() {
        return getDefaultFocalLength()
      },
      get sensorSize() {
        // Calculate sensor size from saved defaults if not provided in EXIF
        const f = getDefaultFocalLength()
        const f35 = getDefaultFocal35mm()
        const diagonal = (43.27 * f) / f35
        return (4 * diagonal) / 5
      },
    }
  }

  /**
   * Convert pixel measurements to real-world centimeters
   * Uses camera settings and distance to calculate actual size
   *
   * @param {Object} pixelMeasurements - Measurements from MeasurementService
   * @param {Object} exifData - Camera metadata from ExifService
   * @param {number} distance - Distance to goat in meters
   * @param {number} angle - Viewing angle in degrees (default 20)
   * @param {number} focalLengthOverride - Override focal length
   * @returns {Object} Real-world measurements in cm
   */
  calibrate(
    pixelMeasurements,
    exifData,
    distance,
    angle = 20,
    focalLengthOverride = null
  ) {
    // Build options object
    const options = this.buildOptions(
      exifData,
      distance,
      angle,
      focalLengthOverride
    )

    // Convert each measurement using thesis formulas
    const lengthCm = this.pixelsToCm(
      this.scaleToWidth(pixelMeasurements.bodyLength, options),
      options
    )

    const shoulderCm = this.pixelsToCm(
      this.scaleToHeight(pixelMeasurements.shoulderHeight, options),
      options
    )

    const rumpCm = this.pixelsToCm(
      this.scaleToHeight(pixelMeasurements.rumpHeight, options),
      options
    )

    const bodyCm = this.pixelsToCm(
      this.scaleToHeight(pixelMeasurements.bodyHeight, options),
      options
    )

    // Calculate accuracy
    const accuracy = this.calculateAccuracy(exifData, distance)

    return {
      widthCm: lengthCm,
      heightCm: bodyCm,
      lengthCm: lengthCm,
      shoulderCm: shoulderCm,
      rumpCm: rumpCm,
      bodyCm: bodyCm,
      calibrationParams: options,
      pixelToCmRatio:
        (options.distance * options.sensorSize) /
        (options.focalLength * options.orig_shape[1]),
      accuracy: accuracy,
    }
  }

  /**
   * Build calibration options object
   */
  buildOptions(exifData, distance, angle, focalLengthOverride) {
    const focalLength =
      focalLengthOverride ||
      exifData.focalLength ||
      this.defaultOptions.focalLength

    const sensorSize = this.getSensorSize(exifData, focalLength)

    const origWidth = exifData.imageWidth || this.defaultOptions.orig_shape[1]
    const origHeight = exifData.imageHeight || this.defaultOptions.orig_shape[0]

    return {
      distance: distance,
      calibration: this.defaultOptions.calibration,
      calibration_distance: this.defaultOptions.calibration_distance,
      orig_shape: [origHeight, origWidth],
      mask_shape: [640, 640], // Always 640x640 for thesis model
      angle: angle,
      sensorSize: sensorSize,
      focalLength: focalLength,
    }
  }

  /**
   * Scale pixels to the width of the picture
   * From thesis utils.ts scaleToWidth()
   *
   * No-op since mask is already 640x640 and measurements are in mask pixels.
   */
  scaleToWidth(pixels, options) {
    return pixels
  }

  /**
   * Scale pixels to the height of the picture
   * From thesis utils.ts scaleToHeight()
   *
   * Applies angle correction using cosine.
   */
  scaleToHeight(pixels, options) {
    const angleFactor = Math.cos((options.angle * Math.PI) / 180)
    return pixels / angleFactor
  }

  /**
   * Convert pixels to centimeters using pinhole camera model.
   * Based on thesis equation: cm = (pixels / reference_resolution) * (sensorSize * distance / focalLength) * 100
   *
   * @param {number} pixels - Measurement in pixels
   * @param {Object} options - Calibration options (distance, sensorSize, focalLength)
   */
  pixelsToCm(pixels, options) {
    // Use 640 as reference resolution since the ML segmentation mask is normalized to 640px
    // Measurements extracted from the mask are proportional to this fixed resolution
    const m =
      ((pixels / 640) * (options.sensorSize * options.distance)) /
      options.focalLength
    return m * 100
  }

  /**
   * Get sensor size from EXIF data or defaults
   *
   * Logic:
   * 1. If FocalLength and 35mm Equivalent are available, calculate diagonal via:
   *    diagonal = (43.27 * focalLength) / focal35mm
   * 2. Convert diagonal to the long-side length (sensor size) for the pinhole model
   *    (Using 4/5 factor for typical mobile 4:3 aspect ratio landscape)
   */
  getSensorSize(exifData, physicalFocal = null) {
    const f = physicalFocal || exifData.focalLength
    const f35 = exifData.focal35mm || exifData.focalLengthIn35mm

    if (f && f35) {
      // Calculate sensor diagonal using 35mm equivalent ratio (Master Thesis Formula)
      const sensorDiagonal = (43.27 * f) / f35

      // The pinhole model used here (pixelsToCm) relates pixels to the sensor's long side
      // For a 4:3 sensor, the long side is 4/5 of the diagonal (Pythagorean 3-4-5)
      const sensorSize = (4 * sensorDiagonal) / 5

      console.info('[Calibration] Dynamic sensor size (long side):', sensorSize)
      return sensorSize
    }

    // Fallback: If physical focal but no 35mm eq, try to match a preset
    if (f) {
      const matchingPreset = focalLengthPresetsData.find(
        (p) => Math.abs(p.focalLength - f) < 0.05
      )
      if (matchingPreset) {
        const sensorDiagonal = (43.27 * f) / matchingPreset.focal35mm
        const sensorSize = (4 * sensorDiagonal) / 5
        console.info(
          `[Calibration] Guessed sensor size ${sensorSize.toFixed(
            2
          )}mm based on focal length ${f}mm`,
          matchingPreset.label
        )
        return sensorSize
      }
    }

    console.info(
      '[Calibration] Falling back to default sensor size:',
      this.defaultOptions.sensorSize
    )
    return this.defaultOptions.sensorSize
  }

  /**
   * Calculate accuracy score based on EXIF and distance quality
   */
  calculateAccuracy(exifData, distance) {
    let accuracy = 1.0

    // EXIF data quality
    if (!exifData.hasFocalLength) {
      accuracy *= 0.85 // Missing focal length reduces accuracy
    }

    // Distance quality
    if (distance < 0.5 || distance > 4) {
      accuracy *= 0.7 // Outside optimal range
    } else if (distance < 1 || distance > 3) {
      accuracy *= 0.9 // Near edge of optimal range
    }

    return accuracy
  }

  /**
   * Set default sensor size
   */
  setDefaultSensorSize(size) {
    this.defaultOptions.sensorSize = size
  }

  /**
   * Set default focal length
   */
  setDefaultFocalLength(length) {
    this.defaultOptions.focalLength = length
  }
}
